import React, { useCallback, useEffect, useState } from 'react';
import _ from 'lodash';

import TopicIdSelectInput from './TopicIdSelectInput';
import withSearchEngine, {
  SearchProps,
} from '../../../search/modules/withSearchEngine';
import * as ElasticSearch from '../../../search/modules/ElasticSearch';
import { defaultIdValue } from './TopicsField';

interface TopicResult {
  id: string;
  text: string;
  href?: string;
}

const connector = ElasticSearch.createConnector('topics-en');
const searchEngineConfig = {
  apiConnector: connector,
  searchQuery: {
    precision: 8,
    search_fields: {
      text: { weight: 10 },
    },
    result_fields: {
      text: {
        raw: {},
        snippet: {},
      },
    },
  },
};

const transformResult = (result): TopicResult => ({
  id: result.id,
  text: result.text,
  href: `#/topics/${result.id}`,
});

interface TopicInputProps {
  record?: string;
  source?: string;
}

type TopicInputWithSearchEngineProps = TopicInputProps &
  SearchProps<TopicResult>;

// <Configure hitsPerPage={3} />

const TopicInput = ({
  source = '',
  record = '',
  search = () => {},
  results,
  clearFilters,
  addFilter,
  setResultsPerPage,
}: TopicInputWithSearchEngineProps) => {
  const [initialTopic, setInitialTopic] = useState<string | undefined>();

  useEffect(() => {
    setResultsPerPage(5);
  }, [setResultsPerPage]);

  const searchWithFilter = useCallback(
    (q: string) => {
      clearFilters();
      search(q);
      // `addFilter` applys `all` for root filter.
      // This is a use case to use `any` for root filter.
    },
    [search, clearFilters],
  );

  useEffect(() => {
    if (
      !_.isEmpty(record) &&
      record !== defaultIdValue &&
      initialTopic == null
    ) {
      clearFilters();
      addFilter('any', { id: record }, 'any');
    }
  }, [record, addFilter, clearFilters, initialTopic]);

  useEffect(() => {
    if (initialTopic == null) {
      const resultForCurrentTopicId = _.find(results, ['id', record]);
      if (resultForCurrentTopicId != null) {
        setInitialTopic(
          resultForCurrentTopicId.text.replace(/<em>|<\/em>/gi, ''),
        );
      }
    }
  }, [initialTopic, record, results]);

  return (
    <TopicIdSelectInput
      key={`topic-input-${initialTopic}`}
      hits={results}
      topicId={record}
      initialTopic={initialTopic}
      source={source}
      search={searchWithFilter}
    />
  );
};

const TopicInputWithSearch = withSearchEngine<
  unknown,
  TopicResult,
  TopicInputProps
>(searchEngineConfig, transformResult, TopicInput);

export default React.memo(TopicInputWithSearch);
